import { ChevronDownIcon, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

/* Hallmark · component: disclosure · genre: modern-minimal · theme: project tokens
 * states: default · hover · focus-visible · open · (disabled/loading/error/success n/a)
 * contrast: pass — title at foreground, hint at muted-foreground on muted/20
 */

/**
 * A disclosure that reads as a control.
 *
 * Every fold in this app used to be a ghost Button wearing the same uppercase
 * micro-caps as the static section labels beside it, with a half-opacity chevron
 * — so nobody knew it opened. The affordance is the bounded surface: an edge that
 * lights up on hover reads as an object you can press; loose text does not. The
 * hint line does the rest, saying what is behind the fold before a click is spent
 * finding out.
 */
export const Disclosure = ({
	icon: Icon,
	title,
	hint,
	children,
	className,
	contentClassName,
}: {
	icon: LucideIcon;
	title: string;
	hint: string;
	children: ReactNode;
	className?: string;
	contentClassName?: string;
}) => (
	<Collapsible className={cn("group rounded-lg border bg-muted/20", className)}>
		<CollapsibleTrigger className="flex w-full items-start gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 group-data-[state=open]:rounded-b-none">
			<Icon className="mt-px size-4 shrink-0 text-muted-foreground" />

			<span className="grid gap-1">
				<span className="text-xs font-semibold leading-none">{title}</span>
				<span className="text-[11px] leading-snug text-muted-foreground text-pretty">
					{hint}
				</span>
			</span>

			<ChevronDownIcon className="mt-px ml-auto size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
		</CollapsibleTrigger>

		<CollapsibleContent className={cn("border-t px-3 py-3", contentClassName)}>
			{children}
		</CollapsibleContent>
	</Collapsible>
);
